import React, { useContext, useEffect, useState } from "react";
import { FormInput, FormSelect } from "@actinc/dls/components";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useQuery } from "urql";

import { UserContext } from "../../../userProvider";

import { DataGrid, NoAccess } from "../../../components";

import { setTitle, sortByName } from "../../../services/utils";

import { AdminUserDialog } from "../UserDialog";

const AdminUsers = () => {
  const user = useContext(UserContext);

  const [dialogState, setDialogState] = useState({
    open: false,
    userID: null,
  });
  const [filters, setFilters] = useState({
    program: "",
    reviewType: "",
    textFilter: "",
  });
  const [programs, setPrograms] = useState();
  const [users, setUsers] = useState();

  const [getProgramsResponse] = useQuery({
    query: getProgramsQuery,
  });
  const [getUsersResponse] = useQuery({
    query: getUsersQuery,
  });

  useEffect(() => {
    setTitle(["CDE", "Admin", "Users"]);
  }, []);

  useEffect(() => {
    const programOptions = getProgramsResponse?.data?.programs
      ?.sort(sortByName)
      .map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    setPrograms(programOptions);
  }, [getProgramsResponse?.data?.programs]);

  useEffect(() => {
    setUsers(getUsersResponse?.data?.users?.filter(filterUsers(filters)) || []);
  }, [getUsersResponse?.data?.users, filters]);

  if (!user?._actions?.includes("manageUsers"))
    return <NoAccess message="You do not have permission to manage users." />;

  const changeFilters = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };
  const closeDialog = () => {
    setDialogState({ open: false, userID: null });
  };
  const openDialog = (selectedRow) => () => {
    setDialogState({ open: true, userID: selectedRow?.id });
  };

  const columns = [
    {
      field: "lastName",
      flex: 1,
      headerName: "Last Name",
      type: "string",
      sortComparator: (v1, v2) =>
        v1?.localeCompare(v2, "en", { sensitivity: "base" }),
    },
    {
      field: "firstName",
      flex: 1,
      headerName: "First Name",
      type: "string",
      sortComparator: (v1, v2) =>
        v1?.localeCompare(v2, "en", { sensitivity: "base" }),
    },
  ];
  const dataGridProps = {
    actions: [
      {
        label: "Edit",
        onClick: openDialog,
      },
    ],
    columns,
    error: getUsersResponse?.error?.message,
    loading: getUsersResponse?.fetching,
    rows: users,
    sortModel: [
      {
        field: "lastName",
        sort: "asc",
      },
      // {
      //   field: "firstName",
      //   sort: "asc",
      // },
    ],
  };
  const dialogProps = {
    closeDialog,
    programs,
    userID: dialogState.userID,
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginX: 2,
          marginY: 1,
        }}
      >
        <Typography variant="h4">Manage Users</Typography>
        <Button
          onClick={openDialog(null)}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create User
        </Button>
      </Box>
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginX: 2,
          marginY: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Box sx={{ flexGrow: 1, margin: 2 }}>
            <FormSelect
              fullWidth={true}
              id="user-filters-program"
              label="Program"
              name="program"
              onChange={changeFilters}
              options={programs}
              value={filters.program}
              variant="standard"
            />
          </Box>
          <Box sx={{ flexGrow: 1, margin: 2 }}>
            <FormSelect
              fullWidth={true}
              id="user-filters-reviewType"
              label="Role"
              name="reviewType"
              onChange={changeFilters}
              options={reviewTypeOptions}
              value={filters.reviewType}
              variant="standard"
            />
          </Box>
          <Box sx={{ flexGrow: 1, margin: 2 }}>
            <FormInput
              fullWidth={true}
              id="user-filters-textFilter"
              label="Text Filter"
              name="textFilter"
              onChange={changeFilters}
              value={filters.textFilter}
            ></FormInput>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <DataGrid {...dataGridProps}></DataGrid>
        </Box>
      </Paper>
      {dialogState.open && <AdminUserDialog {...dialogProps} />}
    </>
  );
};

const getProgramsQuery = `
  query GetPrograms {
    programs {
      id
      name
    }
  }`;
const getUsersQuery = `
  query GetUsers {
    users {
      id
      email
      firstName
      lastName
      programAccess {
        program
        reviewTypes
      }
    }
  }`;

const filterUsers = (filters) => (user) => {
  return (
    (!filters.program ||
      user.programAccess
        .map((access) => access.program)
        .includes(filters.program)) &&
    (!filters.reviewType ||
      user.programAccess
        .reduce(
          (acc, program) => [...new Set([...acc, ...program.reviewTypes])],
          [],
        )
        .includes(filters.reviewType)) &&
    (!filters.textFilter ||
      user.email.toUpperCase().includes(filters.textFilter.toUpperCase()) ||
      user.firstName.toUpperCase().includes(filters.textFilter.toUpperCase()) ||
      user.lastName.toUpperCase().includes(filters.textFilter.toUpperCase()))
  );
};
const reviewTypeOptions = [
  "Accessibility Specialist",
  "Content Specialist",
  "Editor",
  "External Reviewer",
  "Graphic Designer",
  "Item Writer",
].map((name) => ({ label: name, value: name }));

export { AdminUsers, filterUsers, getProgramsQuery, getUsersQuery };
